import { z } from 'zod/v4';

/**
 * One Site ID a supplier holds, with who set it (from the link's history).
 *
 * `provenance` is `'MANUAL'` when a person added the site, `'SYNC'` when the
 * sync linked it (or no history entry names it). `setByName` / `setAt` name
 * the person and the time of a manual add — `null` otherwise — and drive the
 * "manually linked by {user} on {date}" line on the supplier panel.
 *
 * `lockedByAnotherCompany` is `true` when another company added the site and
 * still owns it, so the viewing brand may not remove it (DELTA-2764). It never
 * names that company.
 */
export const AmforiSupplierSiteSchema = z
  .object({
    siteId: z.string(),
    provenance: z.enum(['SYNC', 'MANUAL']),
    setByName: z.string().nullable(),
    setAt: z.string().nullable(),
    lockedByAnotherCompany: z.boolean(),
  })
  .strict();

export type AmforiSupplierSite = z.infer<typeof AmforiSupplierSiteSchema>;

/**
 * Public-facing representation of a supplier's amfori link: its business
 * partner and the Site IDs under it (DELTA-2763), plus the last sync that
 * touched it — persisted as self-reported answers on the supplier company.
 *
 * `linked` is the visibility bit: `true` when the supplier holds a site or was
 * ever synced. An empty link for an authorized caller is a valid resource with
 * `linked: false` (NOT a 404).
 *
 * `provenance` is `'MANUAL'` when a person last wrote the link, else `'SYNC'`.
 * `lockedByAnotherCompany` is `true` when any of its sites is locked for the
 * viewer — the whole-link actions (unlink, switching the partner) need every
 * site to be the viewer's to change.
 */
export const AmforiSupplierLinkResourceSchema = z
  .object({
    companyId: z.string(),
    businessPartnerId: z.string().nullable(),
    sites: z.array(AmforiSupplierSiteSchema),
    lastSyncedAt: z.string().nullable(),
    linked: z.boolean(),
    provenance: z.enum(['SYNC', 'MANUAL']),
    lockedByAnotherCompany: z.boolean(),
  })
  .strict();

export type AmforiSupplierLinkResource = z.infer<
  typeof AmforiSupplierLinkResourceSchema
>;
